"use client";

import { useCallback, useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowRightIcon, SettingsIcon, ChevronDownIcon, CreditCardIcon, GiftIcon, InfoIcon, LogOutIcon, ReceiptIcon, ShieldIcon } from "lucide-react";
import type { SessionUser } from "@/lib/auth-instances";
import { trialDaysLeft as getTrialDaysLeft, formatCredits, billingHref, type AllowanceStatus, type BillingScope, type BillingStatus, type BudgetStatus } from "@/lib/billing";
import { planLabel as planLabelFor } from "@/lib/plan-label";
import { useWsSubscribe } from "@/lib/ws-context";
import { logoutWithReset } from "@/lib/logout";
import { PostHogIdentity } from "./posthog-identity";
import { GOLD_MEDALLION } from "./referrals/gold";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { ThemeToggle } from "./theme-toggle";
import { LanguageToggle } from "./language-toggle";

/** Below this %, surface a status dot on the trigger avatar. Above it, the
 *  number is visual noise — open the dropdown to see the meter.
 *  These thresholds are intentionally tighter than the BudgetMeter's gradient
 *  bands (50/20): the dot is a binary "you should care" alarm, the meter is a
 *  continuous indicator. */
const LOW_BUDGET_THRESHOLD = 25;
const CRITICAL_BUDGET_THRESHOLD = 10;

interface ProfileMenuProps {
  user: SessionUser;
  billing: BillingStatus;
  /** Optional — landing page mounts the menu without a budget loader. */
  budget?: BudgetStatus;
  /** The name of the organization billing/budget describe (null for the
   *  viewer's personal one), so the meter is labeled "shared with <name>". */
  billedToOrgName?: string | null;
  /** Which organization the billing links should open /billing on — the one
   *  the numbers above describe. Absent where no agent or organization is in
   *  view, and /billing then shows the viewer's default. */
  billingScope?: BillingScope | null;
}

function Avatar({ user, initials, size }: { user: SessionUser; initials: string; size: "sm" | "md" }) {
  const dimensions = size === "sm" ? "size-10" : "size-12";
  const text = size === "sm" ? "text-sm" : "text-base";
  return user.picture ? (
     
    // eslint-disable-next-line @next/next/no-img-element -- external avatar URL; next/image needs per-host remotePatterns + fixed dims
    <img
      src={user.picture}
      alt=""
      className={`${dimensions} object-cover`}
      referrerPolicy="no-referrer"
    />
  ) : (
    <span
      className={`${dimensions} bg-primary text-white inline-flex items-center justify-center ${text} font-semibold`}
    >
      {initials}
    </span>
  );
}

/** Corner status dot — composed alongside the trigger Avatar to surface a
 *  low-budget alert without taking dropdown space. Keeps Avatar presentational. */
function BudgetDot({ pct }: { pct: number }) {
  const { t } = useLingui();
  // `--warning` is a brownish-amber in light mode (tuned for body text WCAG)
  // and reads too close to `--destructive` at dot size. amber-400 reads
  // unambiguously yellow against both themes.
  const color = pct <= CRITICAL_BUDGET_THRESHOLD ? "bg-destructive" : "bg-amber-400";
  return (
    <span
      role="status"
      aria-label={t`Daily credits at ${pct}%`}
      className={`absolute -bottom-0.5 -right-0.5 size-2.5 ${color} ring-2 ring-background`}
    />
  );
}

/** Corner dot for referral rewards waiting to be redeemed. The budget alarm
 *  takes the corner when both apply — running out is the more urgent news. */
function RewardDot() {
  const { t } = useLingui();
  return (
    <span
      role="status"
      aria-label={t`You have referral rewards to redeem`}
      className="absolute -bottom-0.5 -right-0.5 size-2.5 bg-success ring-2 ring-background"
    />
  );
}

export function ProfileMenu({ user, billing, budget, billedToOrgName, billingScope = null }: ProfileMenuProps) {
  const { t, i18n } = useLingui();
  const router = useRouter();
  // The standalone header Upgrade button is hidden on phones, so the menu
  // carries the CTA there instead. Same gate as the header button.
  // An organization on a billing agreement has nothing to upgrade to, whatever
  // trial state its row still carries from before the agreement.
  const showUpgrade = !billing.exempt && !billing.agreement && (
    billing.status === "trialing" || billing.status === "canceled" || billing.status === "unpaid"
  );
  // The dropdown's BudgetMeter shows live balance; the server-rendered prop
  // is the source of truth on cold load and after route changes, then WS
  // events drive updates.
  const [remaining, setRemaining] = useState(budget?.remaining ?? 0);
  const [purchasedCredit, setPurchasedCredit] = useState(budget?.purchasedCredit ?? 0);

  // The referral program (V523) has its own page. The menu entry is shown to
  // everyone — the page explains when inviting opens — and carries a badge
  // while rewards wait.
  const hasRewards = user.unredeemedRewards > 0;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resync the WS-driven `remaining` state to the server snapshot whenever the budget prop changes (route change / cold load); not pure-derivable since WS events also mutate it
    if (budget) setRemaining(budget.remaining);
  }, [budget]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- same resync pattern as `remaining` above
    if (budget) setPurchasedCredit(budget.purchasedCredit);
  }, [budget]);

  // On pages without a WebSocket provider (e.g. /billing), the context's
  // no-op subscribe just leaves us on the SSR snapshot.
  useWsSubscribe(
    ["budget_update"],
    useCallback((event) => {
      if (event.type === "budget_update") {
        setRemaining(event.budget_remaining);
        if (typeof event.purchased_credit === "number") setPurchasedCredit(event.purchased_credit);
      }
    }, []),
  );

  const daysLeft = getTrialDaysLeft(billing);
  // Trial ends when the budget is spent OR the window closes — mirror the
  // budget-bounded trial gate, but only claim "ended" when we actually know it.
  const windowClosed = daysLeft !== null && daysLeft <= 0;

  // No pool meter under a billing agreement — the pool is not what it spends —
  // nor once the trial window has closed: whatever grant is left can no longer
  // be spent (budget.ts PRIMARY_AVAILABLE_SQL), and a full meter beside
  // "Trial ended" read as money that was not there.
  const pct = budget && budget.planBudget > 0 && !billing.agreement && !(billing.status === "trialing" && windowClosed)
    ? Math.max(0, Math.min(100, Math.round((remaining / budget.planBudget) * 100)))
    : null;
  // Usage is shown as a percentage in the dropdown meter — never dollars — so the
  // chip just names the phase. No "$X left" here: it would invite the unflattering
  // "$200 trial → $9-or-$28 paid" comparison the % meter exists to avoid.
  const planLabel = i18n._(planLabelFor(billing, budget, remaining));
  const initials = user.name
    ? user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : (user.email[0] ?? "?").toUpperCase();
  const displayName = user.name ?? user.email;

  return (
    <>
    <PostHogIdentity id={user.id} email={user.email} isAdmin={user.isAdmin} />
    <DropdownMenu>
      <DropdownMenuTrigger
        className="shrink-0 inline-flex items-center gap-1.5 group outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label={t`Account menu for ${displayName}`}
      >
        <span className="relative inline-block">
          <Avatar user={user} initials={initials} size="sm" />
          {pct !== null && pct <= LOW_BUDGET_THRESHOLD
            ? <BudgetDot pct={pct} />
            : hasRewards && <RewardDot />}
        </span>
        <ChevronDownIcon
          className="w-3.5 h-3.5 text-foreground-muted group-hover:text-foreground transition"
          aria-hidden
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-[260px] p-0 overflow-hidden">
        <div className="px-3 pt-3 pb-2.5 border-b border-border-subtle">
          <div className="flex items-center gap-3">
            <Avatar user={user} initials={initials} size="md" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
              {user.name && (
                <p className="text-xs text-foreground-muted truncate">{user.email}</p>
              )}
            </div>
          </div>
          <div className="mt-3">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-primary-soft text-primary-soft-foreground text-xs font-medium">
              {planLabel}
            </span>
          </div>
        </div>

        {billing.agreement && (
          <div className="px-3 py-2.5 border-b border-border-subtle">
            {billedToOrgName && (
              <p className="mb-1.5 text-[11px] text-foreground-muted">
                <Trans>Billed to <span className="text-foreground">{billedToOrgName}</span></Trans>
              </p>
            )}
            <AllowanceLine allowance={billing.agreement} />
          </div>
        )}

        {!billing.agreement && (pct !== null || purchasedCredit > 0) && (
          <div className="px-3 py-2.5 border-b border-border-subtle">
            {billedToOrgName && (
              <p className="mb-1.5 text-[11px] text-foreground-muted">
                <Trans>Shared with <span className="text-foreground">{billedToOrgName}</span></Trans>
              </p>
            )}
            {pct !== null && <BudgetMeter pct={pct} hasCredit={purchasedCredit > 0} />}
            {/* Running-low nudge. A member may top up the organization too, and
                the link opens on the organization this meter describes. */}
            {pct !== null && pct <= CRITICAL_BUDGET_THRESHOLD && (
              <Link
                href={billingHref(billingScope, { topup: "1", reason: "low" })}
                className="mt-1.5 block text-[11px] text-amber-500 hover:text-amber-400 transition-colors"
              >
                <Trans>Running low — top up credit →</Trans>
              </Link>
            )}
            {purchasedCredit > 0 && <CreditLine cents={purchasedCredit} />}
          </div>
        )}

        <div className="px-3 py-2.5 flex items-center justify-between gap-3 border-b border-border-subtle">
          <span className="text-sm text-foreground"><Trans>Theme</Trans></span>
          <ThemeToggle />
        </div>

        {/* Its own row: a submenu, not a control in a label row like Theme
            above — see language-toggle.tsx for why the segmented shape did not
            survive a third locale. */}
        <LanguageToggle />

        <div className="p-1">
          {/* Mobile-only Upgrade CTA — the header's standalone button is hidden
              below sm. Inline gradient style beats the item's focus:bg-accent;
              onSelect navigation (not an <a>) avoids the iOS new-tab bug. */}
          {showUpgrade && (
            <DropdownMenuItem
              onSelect={() => { router.push(billingHref(billingScope)); }}
              className="sm:hidden flex items-center justify-center gap-1.5 mb-1 font-semibold uppercase tracking-wide focus:text-white"
              style={{
                background: "linear-gradient(to right, var(--primary), var(--primary-active))",
                color: "white",
              }}
            >
              <ArrowRightIcon className="w-3.5 h-3.5 text-white" />
              <span><Trans>Upgrade</Trans></span>
            </DropdownMenuItem>
          )}
          {/* A wash of the rewards' gold, so the entry stands out from the plain
              rows without moving. The wash is a background image, which would
              hide the item's highlight color, so it brings its own highlight. */}
          <DropdownMenuItem
            asChild
            className="font-medium text-amber-900 bg-gradient-to-r from-amber-100 via-yellow-50 to-amber-100 focus:text-amber-950 focus:from-amber-200 focus:via-yellow-100 focus:to-amber-200 data-[highlighted]:text-amber-950 data-[highlighted]:from-amber-200 data-[highlighted]:via-yellow-100 data-[highlighted]:to-amber-200 dark:text-amber-200 dark:from-amber-900/40 dark:via-yellow-900/20 dark:to-amber-900/40 dark:focus:text-amber-100 dark:focus:from-amber-800/50 dark:focus:via-yellow-800/30 dark:focus:to-amber-800/50 dark:data-[highlighted]:text-amber-100 dark:data-[highlighted]:from-amber-800/50 dark:data-[highlighted]:via-yellow-800/30 dark:data-[highlighted]:to-amber-800/50"
          >
            <Link href={hasRewards ? "/referrals#people" : "/referrals"}>
              {/* Negative margins keep the label in line with the rows' 16px icons. */}
              <span className={`${GOLD_MEDALLION} -mx-0.5 size-5`}>
                <GiftIcon className="size-3 text-white" aria-hidden />
              </span>
              <span className="flex-1"><Trans>Referrals</Trans></span>
              {hasRewards && (
                <span className="rounded-full bg-gradient-to-br from-yellow-300 via-amber-400 to-amber-500 px-1.5 text-[11px] font-semibold text-amber-950 ring-1 ring-amber-500/40">
                  <Trans>Reward</Trans>
                </span>
              )}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/account" className="flex items-center gap-2">
              <SettingsIcon className="w-4 h-4" />
              <span><Trans>Account Settings</Trans></span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href={billingHref(billingScope)} className="flex items-center gap-2">
              <CreditCardIcon className="w-4 h-4" />
              <span><Trans>Billing</Trans></span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/billing/history" className="flex items-center gap-2">
              <ReceiptIcon className="w-4 h-4" />
              <span><Trans>Payment history</Trans></span>
            </Link>
          </DropdownMenuItem>
          {user.isAdmin && (
            <DropdownMenuItem asChild>
              <Link href="/admin" className="flex items-center gap-2">
                <ShieldIcon className="w-4 h-4" />
                <span><Trans>Admin</Trans></span>
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => { logoutWithReset(); }}
            className="flex items-center gap-2"
          >
            <LogOutIcon className="w-4 h-4" />
            <span><Trans>Sign out</Trans></span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>

    </>
  );
}

/** Top-up credits line under the daily-credits meter. Shown whenever purchased
 *  credits exist — including with NO active plan (pay-as-you-go is a
 *  first-class way to be a customer). Unlike the plan pool, this is shown as a
 *  figure, not a share: it's credits the user explicitly bought, not a
 *  plan-relative quota. */
function CreditLine({ cents }: { cents: number }) {
  const { i18n } = useLingui();
  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-foreground-muted"><Trans>Top-up credits</Trans></span>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="opacity-40 hover:opacity-80 transition-opacity">
              <InfoIcon size={11} className="text-foreground-muted" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[240px] text-xs leading-relaxed">
            <Trans>Credits you bought with top-ups. Your plan&rsquo;s daily credits are always used first — top-up credits only spend after they run out. They never expire and keep your agents working even without an active plan.</Trans>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <span className="ml-auto font-mono text-[10px] font-bold text-success">{formatCredits(cents, i18n)}</span>
    </div>
  );
}

/** Why agents are paused, in the room the menu has. */
function AllowancePauseNote({ allowance }: { allowance: AllowanceStatus }) {
  if (allowance.whenExhausted === "continue") {
    return <Trans>Monthly limit reached — agents are paused until it&rsquo;s raised or next month.</Trans>;
  }
  return allowance.quotaMode === "refill"
    ? <Trans>Today&rsquo;s quota used up — agents are paused until tonight&rsquo;s refill or a fill-up.</Trans>
    : <Trans>Allowance used up — agents are paused until it&rsquo;s filled up or next month.</Trans>;
}

/** An organization's month under its agreement, as a share of the allowance —
 *  a percentage like the pool meter, and for the same reason. */
function AllowanceLine({ allowance }: { allowance: AllowanceStatus }) {
  const pct = allowance.allowanceUsedPercent;
  return (
    <div>
      <p className="text-xs text-foreground-muted">
        {pct === null
          ? <Trans>Billed by usage this month</Trans>
          : allowance.quotaMode === "refill"
            ? <Trans>{pct}% of a month&rsquo;s refill used</Trans>
            : <Trans>{pct}% of this month&rsquo;s allowance used</Trans>}
      </p>
      {allowance.paused && (
        <p className="mt-1 text-[11px] text-amber-500"><AllowancePauseNote allowance={allowance} /></p>
      )}
    </div>
  );
}

function BudgetMeter({ pct, hasCredit = false }: { pct: number; hasCredit?: boolean }) {
  const hue = pct > 50 ? 190 : pct > 20 ? 40 : 0;
  const color = `hsl(${hue}, 80%, 55%)`;
  const colorDim = `hsl(${hue}, 60%, 35%)`;

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-foreground-muted"><Trans>Budget</Trans></span>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="opacity-40 hover:opacity-80 transition-opacity">
                <InfoIcon size={11} className="text-foreground-muted" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[240px] text-xs leading-relaxed">
              {hasCredit
                ? <Trans>Budget is spent when any of your agents thinks or works — every agent you&apos;ve hired draws from the same pool. It tops up daily and is always used first; when it runs out, your top-up credit takes over.</Trans>
                : <Trans>Budget is spent when any of your agents thinks or works — every agent you&apos;ve hired draws from the same pool. It tops up daily. When it runs out, agents pause until the next top-up.</Trans>}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className="ml-auto font-mono text-[10px] font-bold" style={{ color }}>{pct}%</span>
      </div>
      <div
        className="relative h-1.5 overflow-hidden"
        style={{
          background: "rgba(0,0,0,0.04)",
          border: `1px solid ${colorDim}`,
        }}
      >
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${colorDim}, ${color})`,
            boxShadow: `0 0 8px ${color}`,
          }}
        />
      </div>
    </div>
  );
}
